import React from 'react';
import ReactDOM from 'react-dom/client';

import './css/style.css';
import { ErrorBoundary } from "react-error-boundary";
import { getCookie } from './functions/getCookie';
import FormBlock from './components/Form/FormBlock';
import { PartSlot } from './partSlot';
import { componentRegistry, readJsonScriptData } from './componentRegistry';
import { ProductForm } from './WebsiteComponents/Store';

const BOOTSTRAP_RGB_NAMES = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark'];

const syncBootstrapRgb = () => {
  const root = document.documentElement;
  const probe = document.createElement('span');
  probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;';
  (document.body || root).appendChild(probe);
  BOOTSTRAP_RGB_NAMES.forEach((name) => {
    probe.style.color = `var(--bs-${name})`;
    const color = getComputedStyle(probe).color || '';
    const match = color.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/);
    if (!match) return;
    root.style.setProperty(
      `--bs-${name}-rgb`,
      `${Math.round(Number(match[1]))}, ${Math.round(Number(match[2]))}, ${Math.round(Number(match[3]))}`,
    );
  });
  probe.remove();
};

if (document.body) syncBootstrapRgb();
document.addEventListener('DOMContentLoaded', syncBootstrapRgb);

var csrftoken = getCookie('csrftoken');

const fallback = <h6 class="text-center my-5 ">Helaas ging er iets fout bij ons. Neem aub contact met ons op.</h6>

const errorLogging = (error, info) => {
  var body = JSON.stringify(
      'JS ERROR: url: ' + window.location + '. error: '+ error.toString() + '. Component: '+ (info && info.componentStack ? info.componentStack : '')
  )
  fetch('/server/error/', {
      method:'POST',
      body:body,
      credentials: 'same-origin',
      headers: {
          'X-CSRFToken': getCookie('csrftoken') || csrftoken || '',
          'X-Requested-With':'XMLHttpRequest',
      }
  }).catch(() => {})
}

const getPrefixedAttr = (el, name) =>
  el.getAttribute(`umr-${name}`) || el.getAttribute(`britge-${name}`);

const getPrefixedId = (name) =>
  document.getElementById(`umr-${name}`) || document.getElementById(`britge-${name}`);

const normalizeComponentKind = (kind) => {
  if (!kind) return '';
  return String(kind).replace(/^(umr|britge)-/, '');
}

const umrRoots = new WeakMap();

const renderInto = (el, node) => {
  let root = umrRoots.get(el);
  if (!root) {
    root = ReactDOM.createRoot(el);
    umrRoots.set(el, root);
  }
  root.render(
    <ErrorBoundary fallback={fallback} onError={errorLogging} >
      {node}
    </ErrorBoundary>
  );
};

const renderUmrComponent = (component) => {
  const kind = normalizeComponentKind(getPrefixedAttr(component, 'component'));
  const entry = componentRegistry[kind];
  if (!entry) return;
  const Component = entry.Component;
  const props = entry.mapProps(component) || {};
  renderInto(component, <Component {...props} />);
};

const reactSlotFallback = {
  'product-form': ProductForm,
  ProductForm,
};

const renderReactSlot = (el) => {
  const id = el.getAttribute('data-umr-react-component');
  if (!id || getPrefixedAttr(el, 'component')) return;
  let extra = {};
  try {
    extra = JSON.parse(el.getAttribute('data-umr-props') || '{}') || {};
  } catch (e) {
    extra = {};
  }
  const initialProduct = extra.initialProduct
    || readJsonScriptData(el.dataset.initialDataId)
    || readJsonScriptData('product-detail-data');
  const pathId = (window.location.pathname.match(/\/(\d+)\/?$/) || [])[1];
  const props = {
    ...extra,
    productId: extra.productId || el.dataset.productId || initialProduct?.id || pathId || null,
    initialProduct: extra.initialProduct || initialProduct,
    locationSearch: extra.locationSearch || window.location.search,
  };
  const Fallback = reactSlotFallback[id];
  renderInto(el, (
    <PartSlot id={id} props={props}>
      {Fallback ? <Fallback {...props} /> : null}
    </PartSlot>
  ));
};

const promoteLegacyMountIds = () => {
  const confirm = getPrefixedId('reserve-confirm');
  if (confirm && !getPrefixedAttr(confirm, 'component')) {
    confirm.setAttribute('umr-component', 'reservation-overview');
  }
  const confirmed = getPrefixedId('reserve-confirmed');
  if (confirmed && !getPrefixedAttr(confirmed, 'component')) {
    confirmed.setAttribute('umr-component', 'reservation-confirmed');
  }
};

const mountUmrComponents = (root = document) => {
  const scope = root?.querySelectorAll ? root : document;
  scope.querySelectorAll('[umr-component], [britge-component]').forEach(renderUmrComponent);
  scope.querySelectorAll('[data-umr-react-component]').forEach(renderReactSlot);
};

window.mountUmrComponents = mountUmrComponents;
window.mountBritgeComponents = mountUmrComponents;
promoteLegacyMountIds();
mountUmrComponents();

function resolveFormInitialDataId(el) {
  if (el.dataset.initialDataId) return el.dataset.initialDataId;
  const url = el.dataset.url || '';
  if (url.includes('/reservation/add_store/')) return 'reservation-form-data';
  return null;
}

async function refreshOrderSummary() {
  const current = document.querySelector('.order-summary-card');
  if (!current) return;
  const response = await fetch(window.location.href, { credentials: 'same-origin' });
  if (!response.ok) return;
  const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
  const next = doc.querySelector('.order-summary-card');
  if (!next) return;
  const panelId = '#order-lines-products-panel';
  if (!current.querySelector(panelId)?.classList.contains('show')) {
    next.querySelector(panelId)?.classList.remove('show');
    next.querySelector('[data-bs-target="' + panelId + '"]')?.setAttribute('aria-expanded', 'false');
    next.querySelector('.order-lines-chevron')?.classList.replace('bi-chevron-up', 'bi-chevron-down');
  }
  current.replaceWith(next);
}

function mountUmrFormElement(el) {
  if (!el || !el.dataset || !el.dataset.url || el.dataset.umrFormMounted || el.dataset.britgeFormMounted) return;
  el.dataset.umrFormMounted = '1';
  const order_form = ReactDOM.createRoot(el);
  order_form.render(
      <ErrorBoundary fallback={fallback} onError={errorLogging} >
        <FormBlock
          url={el.dataset.url}
          initialData={readJsonScriptData(resolveFormInitialDataId(el))}
          disappear={el.dataset.disappear === 'true'}
          saveHook={
            el.dataset.reload === 'true'
              ? () => window.location.reload()
              : (forms, index) => { if (forms[index]?.autoSaved) refreshOrderSummary(); }
          }
        />
      </ErrorBoundary>
  );
}

document.querySelectorAll('.umr-form, .britge-form').forEach(mountUmrFormElement);

window.__umrMountForm = mountUmrFormElement;
window.__britgeMountForm = mountUmrFormElement;
